import { useCallback, useEffect, useMemo, useState } from "react"
import api from "../../components/utils/api"
import Spinner from "../../components/template/Spinner"
import TrainingReportLineChart from "../TrainingReportPage/TrainingReportLineChart"
import { chartColor } from "../TrainingReportPage/chartColors"

const METRIC_OPTIONS = [
    { key: "val_loss", label: "Validation Loss" },
    { key: "val_fscore", label: "Validation F-Score" },
    { key: "val_precision", label: "Validation Precision" },
    { key: "val_recall", label: "Validation Recall" },
    { key: "tr_loss", label: "Training Loss" },
    { key: "tr_fscore", label: "Training F-Score" },
    { key: "tr_precision", label: "Training Precision" },
    { key: "tr_recall", label: "Training Recall" },
    { key: "lr", label: "Learning Rate" },
    { key: "tr_time", label: "Training Time" },
    { key: "val_time", label: "Validation Time" },
]

const MODE_LABELS = { single: "Single", distill: "Distill" }

// Recorded trainings are labeled by their settings; external runs by their
// folder name with the experiment-wide "{db_version}_{exp_ver}_" prefix
// stripped, since every run of the page shares it.
const runLabel = (run) => {
    if (run.training) {
        const mode = MODE_LABELS[run.training.mode] || run.training.mode
        return `${mode} ${run.training.architecture} ${run.training.sample_rate} · ${run.training.session_id}`
    }
    return run.model_name.replace(/^.+?_v\d+_/, "")
}

// Debiased exponential moving average, the same smoothing TensorBoard applies:
// higher weight follows the curve's trend, 0 leaves it untouched.
const smooth = (values, weight) => {
    let last = 0
    return values.map((value, index) => {
        last = last * weight + (1 - weight) * value
        return last / (1 - Math.pow(weight, index + 1))
    })
}

const faded = (hex, alpha) => {
    const n = parseInt(hex.slice(1), 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

// Overlays one metric of every run of an experiment on a single chart, so an
// in-progress training can be judged against its siblings early. The header
// (title, refresh) belongs to the page; bumping `refreshKey` refetches.
const TrainingComparison = ({ experimentId, refreshKey }) => {
    const [runs, setRuns] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [isLoadFailed, setIsLoadFailed] = useState(false)
    const [isModelsListAvailable, setIsModelsListAvailable] = useState(true)
    const [selectedMetric, setSelectedMetric] = useState("val_loss")
    const [smoothing, setSmoothing] = useState(0)

    const fetchMetrics = useCallback(async () => {
        setIsLoading(true)
        setIsLoadFailed(false)
        try {
            const res = await api.get(`/api/experiments/${experimentId}/training-metrics`)
            setRuns(res.data.runs)
            setIsModelsListAvailable(res.data.models_list_available)
        } catch (error) {
            console.error(error)
            setIsLoadFailed(true)
        } finally {
            setIsLoading(false)
        }
    }, [experimentId])

    useEffect(() => { fetchMetrics() }, [fetchMetrics, refreshKey])

    // old loss.tsv formats lack lr/time columns, so only offer metrics some run carries
    const availableMetrics = useMemo(() => METRIC_OPTIONS.filter(({ key }) =>
        runs.some(run => run.metrics.some(entry => entry[key] != null))
    ), [runs])

    const metricKey = availableMetrics.some(option => option.key === selectedMetric)
        ? selectedMetric
        : availableMetrics[0]?.key

    const traces = useMemo(() => {
        const smoothed = []
        const originals = []
        runs.filter(run => run.metrics.length > 0).forEach((run, index) => {
            const entries = run.metrics.filter(entry => entry[metricKey] != null)
            if (entries.length === 0) return
            const x = entries.map(entry => entry.epoch)
            const y = entries.map(entry => entry[metricKey])
            const isLive = run.status === "in_progress"
            const name = isLive ? `${runLabel(run)} (live)` : runLabel(run)
            const dash = isLive ? "dash" : undefined
            smoothed.push({
                x,
                y: smoothing > 0 ? smooth(y, smoothing) : y,
                mode: "lines",
                name,
                line: { color: chartColor(index), dash },
            })
            if (smoothing > 0) originals.push({
                x,
                y,
                mode: "lines",
                name,
                showlegend: false,
                hoverinfo: "skip",
                line: { color: faded(chartColor(index), 0.15), dash },
            })
        })
        // originals first, so the smoothed lines draw on top of them
        return [...originals, ...smoothed]
    }, [runs, metricKey, smoothing])

    const failedRuns = runs.filter(run => run.error)
    const metricLabel = METRIC_OPTIONS.find(option => option.key === metricKey)?.label

    return (
        <div>
            <div className="flex justify-end items-center">
                {availableMetrics.length > 0 && <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-500" title="Exponential moving average smoothing">
                        Smoothing
                        <input
                            type="range"
                            min="0"
                            max="0.95"
                            step="0.05"
                            value={smoothing}
                            onChange={event => setSmoothing(Number(event.target.value))}
                        />
                        <span className="w-8 text-left tabular-nums">{smoothing.toFixed(2)}</span>
                    </label>
                    <select
                        value={metricKey}
                        onChange={event => setSelectedMetric(event.target.value)}
                        title="Metric to compare"
                        className="border rounded px-2 py-1 text-sm text-gray-700"
                    >
                        {availableMetrics.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
                    </select>
                </div>}
            </div>
            {!isModelsListAvailable && <div className="my-2 text-left text-sm text-gray-400">
                The model listing could not be reached — only the trainings recorded here are compared.
            </div>}
            {isLoading
                ? <div className="my-3"><Spinner /></div>
                : isLoadFailed
                ? <div className="my-3 text-sm text-red-500">
                    Failed to load the training metrics. Use the refresh button to try again.
                </div>
                : traces.length === 0
                ? <div className="my-3 text-sm text-gray-500">No training metrics to compare yet.</div>
                : <TrainingReportLineChart data={traces} name={metricLabel} height={500} />}
            {failedRuns.length > 0 && <div className="my-2 text-left text-xs text-gray-400">
                {failedRuns.map(run => <div key={run.model_name}>Not shown: {run.model_name} — {run.error}</div>)}
            </div>}
        </div>
    )
}

export default TrainingComparison
